import { Injectable } from '@angular/core';
import { ROUTES_MAP_BY_FEATURE } from '@environment';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, forkJoin, from, Observable, of, timer } from 'rxjs';
import {
    catchError,
    concatMap,
    defaultIfEmpty,
    exhaustMap,
    map,
    mergeMap,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { AuthActions, AuthFlow, AuthService, currentUser$, User } from '@mona/auth';
import { ChangeLogEntry, ChangeLogModel, Encounter } from '@mona/models';
import { currentEncounterId, withCurrentEncounterId } from '@mona/pdms/data-access-combined';
import { selectSelectedEncounter } from '@mona/pdms/data-access-encounters';
import { AppError, isSameEncounterFilter } from '@mona/shared/utils';
import { makeDefaultAsyncActionEffect, RouterActions, selectRouteParam } from '@mona/store';
import { MessageService } from '@mona/ui';
import { ChangeLogService } from '../../application';
import { ChangeLogApi, extendPayloadWithMetaData } from '../../infrastructure';
import { ChangeLogAction } from '../actions';
import * as ChangeLogSelectors from '../selectors';

/**
 * Change log effects
 */
@Injectable({ providedIn: 'root' })
export class ChangeLogEffects {
    // exhaustMap + defaultIfEmpty(clearAction) ensures inProgress always resets.
    persistChanges$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeLogAction.persistChangesAction.action),
            withLatestFrom(this.authService.user$),
            exhaustMap(([, userBeforeLogin]) =>
                // If user enters credentials - verify happens via relogin, but for rfid verify and login are separate endpoint.
                // In case of rfid verify here we're expecting error 409 which means conflict and then we have to do rfid login.
                this.authService.authenticate(AuthFlow.verify).pipe(
                    map(
                        ({ user: userAfterLogin }) =>
                            [userBeforeLogin, userAfterLogin] as [User, User | null | undefined],
                    ),
                    take(1),
                    defaultIfEmpty([userBeforeLogin, null] as [User, User | null | undefined]),
                    concatLatestFrom(() => this.store.select(selectRouteParam('encounterId'))),
                    concatLatestFrom(() => this.store.select(ChangeLogSelectors.getChanges)),
                    concatMap(([[userPair, encounterId], sortedChanges]) => {
                        const [userBefore, userAfterLogin] = userPair;
                        if (!userAfterLogin || !encounterId) {
                            return [ChangeLogAction.persistChangesAction.clearAction()];
                        }

                        const hasAllRequiredPermissions =
                            this.changeLogService.checkPendingChangesModelsPermissions(sortedChanges);
                        const hasUserChangedDuringPersist = userBefore.id !== userAfterLogin.id;

                        if (!hasAllRequiredPermissions) {
                            if (hasUserChangedDuringPersist) {
                                this.authService.logOut(
                                    new AppError('No permissions'),
                                    true,
                                    hasUserChangedDuringPersist,
                                );
                                // INFO: explicit clearAction so the per-effect tail
                                // `defaultIfEmpty` is not the only line of defence.
                                return [ChangeLogAction.persistChangesAction.clearAction()];
                            }

                            // INFO: if user does not have permission to edit some model we redirect to "Access Denied" page and show warning alert.
                            this.messageService.warnToast(
                                this.translateService.instant('warnings.notEnoughtPermissionsToSavePendingChanges'),
                            );

                            this.store.dispatch(
                                RouterActions.navigateAction({
                                    path: [ROUTES_MAP_BY_FEATURE.UNAUTHORIZED],
                                    extras: {
                                        state: { skipDiscardDialog: true },
                                    },
                                }),
                            );

                            return [
                                ChangeLogAction.persistChangesAction.failedAction({
                                    error: new AppError('No permissions'),
                                }),
                            ];
                        }

                        return makeDefaultAsyncActionEffect(
                            this.changeLogApi.persistChanges(userAfterLogin.rfid, encounterId, userBefore.id).pipe(
                                tap(() => {
                                    if (hasUserChangedDuringPersist) {
                                        this.authService.logOut(null, true, hasUserChangedDuringPersist);
                                    }
                                }),
                            ),
                            ChangeLogAction.persistChangesAction,
                        );
                    }),
                    defaultIfEmpty(
                        ChangeLogAction.persistChangesAction.clearAction() as ReturnType<
                            typeof ChangeLogAction.persistChangesAction.clearAction
                        >,
                    ),
                ),
            ),
        ),
    );
    private static readonly RECONCILE_DELAY_MS = 5_000;

    // Re-fetch affected model types after persist to cover dropped WS pushes.
    reconcileAfterPersist$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeLogAction.persistChangesAction.succeededAction),
            withLatestFrom(this.store.select(ChangeLogSelectors.getChangesMap)),
            map(([action, changesMap]) => {
                const succeededEntryIds = new Set(action.payload.succeeded || []);
                if (succeededEntryIds.size === 0) {
                    return [] as string[];
                }
                return Object.keys(changesMap).filter(modelType =>
                    (changesMap[modelType] || []).some(entry => succeededEntryIds.has(entry.id)),
                );
            }),
            concatMap(modelTypes =>
                modelTypes.length
                    ? timer(ChangeLogEffects.RECONCILE_DELAY_MS).pipe(
                          map(() => ChangeLogAction.reconcileModelsAction({ modelTypes })),
                      )
                    : EMPTY,
            ),
        ),
    );

    /**
     * Persist changes succeeded effect
     */
    persistChangesSucceeded$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withCurrentEncounterId(),
                tap(([action, encounterId]) => {
                    const failedItems = action.payload.failed;
                    if (failedItems?.length) {
                        this.messageService.errorToast('errors.stageChanges', {
                            originalError: {
                                description: 'Failed items ids and reasons',
                                failedItems,
                            },
                        });
                    }

                    if (action.payload.succeeded?.length) {
                        this.messageService.successToast('apps.settings.messages.saveSuccess');
                    }

                    this.store.dispatch(
                        ChangeLogAction.clearPersistedSucceededModels({ ids: action.payload.succeeded }),
                    );
                }),
                tap(() => localStorage.removeItem('changeLogTags')),
            ),
        { dispatch: false },
    );

    /* Effect Declarations */
    /** Selector for current encounter  */
    private readonly currentEncounter$: Observable<Encounter> = this.store.select(selectSelectedEncounter);
    /**
     * Load changes effect
     *
     * Should get `encounterId` from current state if not provided
     */
    loadChanges$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeLogAction.loadChangesAction.action),
            withLatestFrom(this.currentEncounter$.pipe(map(e => e?.id))),
            switchMap(([action, encounterId]) =>
                makeDefaultAsyncActionEffect(
                    this.changeLogApi.loadChanges(encounterId || action.encounterId),
                    ChangeLogAction.loadChangesAction,
                ),
            ),
        ),
    );
    saveChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeLogAction.saveChangeAction.action),
            concatLatestFrom(() => [this.currentEncounter$, this.authService.user$]),
            map(([{ change }, encounter, practitioner]) => ({
                ...change,
                payload: extendPayloadWithMetaData(change.payload, encounter, practitioner),
            })),
            concatLatestFrom(() => this.store.select(selectRouteParam('encounterId'))),
            mergeMap(([change, encounterId]) => {
                if (!encounterId) {
                    return [ChangeLogAction.saveChangeAction.clearAction()];
                }
                return from(
                    makeDefaultAsyncActionEffect(
                        this.changeLogApi
                            .saveChange(change, change.payload.encounterId)
                            .pipe(isSameEncounterFilter(currentEncounterId(this.store), encounterId)),
                        ChangeLogAction.saveChangeAction,
                    ),
                ).pipe(defaultIfEmpty(ChangeLogAction.saveChangeAction.clearAction()));
            }),
        ),
    );

    saveChanges$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeLogAction.saveChangesAction.action),
            concatLatestFrom(() => [this.currentEncounter$, this.authService.user$]),
            map(([{ changes }, encounter, practitioner]) =>
                changes.map((change, idx) => ({
                    ...change,
                    payload: extendPayloadWithMetaData(change.payload, encounter, practitioner, idx),
                })),
            ),
            concatLatestFrom(() => this.store.select(selectRouteParam('encounterId'))),
            switchMap(([changes, encounterId]) => {
                if (!encounterId) {
                    return [ChangeLogAction.saveChangesAction.clearAction()];
                }
                return from(
                    makeDefaultAsyncActionEffect(
                        forkJoin(
                            changes.map(change => this.changeLogApi.saveChange(change, change.payload.encounterId)),
                        ).pipe(isSameEncounterFilter(currentEncounterId(this.store), encounterId)),
                        ChangeLogAction.saveChangesAction,
                    ),
                ).pipe(defaultIfEmpty(ChangeLogAction.saveChangesAction.clearAction()));
            }),
        ),
    );
    /** current practitioner */
    private readonly practitioner$ = currentUser$();
    // exhaustMap + defaultIfEmpty(clearAction) ensures inProgress always resets.
    discardChanges$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeLogAction.discardChangesAction.action),
            withLatestFrom(this.practitioner$),
            exhaustMap(([{ ids }, previousPractitioner]) => {
                return (
                    ids?.length
                        ? this.practitioner$
                        : this.authService.authenticate(AuthFlow.verify).pipe(map(({ user }) => user))
                ).pipe(
                    take(1),
                    defaultIfEmpty(null as User | null),
                    withLatestFrom(this.currentEncounter$.pipe(map(e => e?.id))),
                    concatMap(([user, encounterId]) => {
                        // FIXME: FIXME: FIXME: FIXME: FIXME: REFACTOR REDUCER TO ENTITY ADAPTER
                        // TODO: TODO: TODO: TODO: TODO: TODO: REFACTOR REDUCER TO ENTITY ADAPTER
                        if (user) {
                            if (!ids?.length) {
                                return makeDefaultAsyncActionEffect(
                                    this.changeLogApi.discardChanges(encounterId, ids, previousPractitioner.id),
                                    ChangeLogAction.discardChangesAction,
                                );
                            }
                            return this.changeLogApi.discardChanges(encounterId, ids, previousPractitioner.id).pipe(
                                withLatestFrom(this.store.select(ChangeLogSelectors.getSortedChanges)),
                                concatMap(([, sortedChanges]) => {
                                    const idsSet = new Set(ids);
                                    const payload = sortedChanges.filter(change => !idsSet.has(change.modelId));
                                    return [
                                        ChangeLogAction.loadChangesAction.succeededAction({ payload }),
                                        ChangeLogAction.discardChangesAction.clearAction(),
                                    ];
                                }),
                                catchError(error => of(ChangeLogAction.discardChangesAction.failedAction({ error }))),
                            );
                        } else {
                            return [ChangeLogAction.discardChangesAction.clearAction()];
                        }
                    }),
                    defaultIfEmpty(
                        ChangeLogAction.discardChangesAction.clearAction() as ReturnType<
                            typeof ChangeLogAction.discardChangesAction.clearAction
                        >,
                    ),
                );
            }),
            tap(() => localStorage.removeItem('changeLogTags')),
        ),
    );

    /**
     * Constructor
     *
     * @param store Store<EncounterFeatureState>,
     * @param actions$ Actions
     * @param changeLogApi ChangeLogApi
     * @param messageService MessageService
     * @param authService AuthService
     * @param changeLogService ChangeLogService
     * @param translateService TranslateService
     */
    constructor(
        private store: Store<any>,
        private actions$: Actions,
        private changeLogApi: ChangeLogApi,
        private messageService: MessageService,
        private authService: AuthService,
        private changeLogService: ChangeLogService,
        private translateService: TranslateService,
    ) {}
}
